import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, Tag, Lock } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';

// Edit modal for a saved recording's metadata. Title + BPM are always
// editable. Notes + Tags are PRO-only and show a locked card for FREE.

const EditMetadataModal = ({
  editingRecording,
  editForm,
  setEditForm,
  newTag,
  setNewTag,
  savingEdit,
  isPremium,
  onClose,
  onSave,
  onAddTag,
  onRemoveTag,
}) => {
  const { t } = useTranslation();
  if (!editingRecording) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-[#121212] border border-white/10 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-xl font-bold text-white">Modifica Registrazione</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-400">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Titolo</label>
            <Input
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              className="bg-[#0A0A0A] border-white/10 text-white"
              data-testid="edit-title"
            />
          </div>

          <div>
            <label className="text-sm text-zinc-400 mb-1 block">BPM</label>
            <Input
              type="number"
              value={editForm.bpm}
              onChange={(e) => setEditForm({ ...editForm, bpm: e.target.value })}
              placeholder="120"
              className="bg-[#0A0A0A] border-white/10 text-white"
              data-testid="edit-bpm"
            />
          </div>

          <div>
            <label className="text-sm text-zinc-400 mb-1 block flex items-center gap-2">
              Note
              {!isPremium && <Lock className="w-3 h-3 text-zinc-600" />}
            </label>
            {isPremium ? (
              <textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder={t('recorder.notesPlaceholder')}
                rows={3}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-md text-white p-3 text-sm resize-none"
                data-testid="edit-notes"
              />
            ) : (
              <div className="bg-zinc-800/50 border border-white/5 rounded-md p-3 text-center">
                <Lock className="w-4 h-4 text-zinc-600 mx-auto mb-1" />
                <p className="text-zinc-500 text-xs">Solo PRO</p>
              </div>
            )}
          </div>

          <div>
            <label className="text-sm text-zinc-400 mb-1 block flex items-center gap-2">
              <Tag className="w-3 h-3" /> Tag
              {!isPremium && <Lock className="w-3 h-3 text-zinc-600" />}
            </label>
            {isPremium ? (
              <div>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && onAddTag()}
                    placeholder={t('recorder.tagsPlaceholder')}
                    className="bg-[#0A0A0A] border-white/10 text-white flex-1"
                    data-testid="edit-new-tag"
                  />
                  <Button onClick={onAddTag} size="sm" className="bg-purple-500 text-white">
                    +
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {editForm.tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-sm flex items-center gap-1"
                    >
                      {tag}
                      <button onClick={() => onRemoveTag(tag)} className="hover:text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-zinc-800/50 border border-white/5 rounded-md p-3 text-center">
                <Lock className="w-4 h-4 text-zinc-600 mx-auto mb-1" />
                <p className="text-zinc-500 text-xs">Solo PRO</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={onSave}
            disabled={savingEdit || !editForm.title.trim()}
            className="flex-1 bg-gradient-to-r from-amber to-orange-500 text-black font-semibold"
            data-testid="save-metadata-btn"
          >
            {savingEdit ? (
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              'Salva'
            )}
          </Button>
          <Button onClick={onClose} variant="ghost" className="text-zinc-400">
            Annulla
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EditMetadataModal;
